/**
 * Tour-level fund wallet shown under Expenses (day/leg ledger).
 * Same pot for every day of the tour — contribute, disburse, ledger.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Linking,
  Platform,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db, functions } from '../../config/firebase';
import { getDeployTarget } from '../../config/environment';
import { formatFundsCents } from '../../constants/userFunds';
import { grossUpCardChargeCents } from '../../utils/stripeCardFees';
import { searchUsersForShare } from '../../utils/calendarShareSendService';
import { palette, FONT } from '../../styles/tourTheme';

function pageOrigin() {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return null;
}

function ledgerTypeLabel(type) {
  if (type === 'contribution') return 'In · Contribution';
  if (type === 'disbursement') return 'Out · Disburse';
  if (type === 'payout') return 'Out · Payout';
  return type || 'Movement';
}

export default function TourFundSettlementSection({
  tourId,
  tourName = '',
  isDarkMode,
  accent,
  /** Optional dollars to prefill contribute amount */
  suggestedDollars = null,
}) {
  const pal = palette(isDarkMode);
  const hue = accent || pal.teal;
  const authUser = getAuth().currentUser;
  const deployTarget = getDeployTarget();

  const [tourFund, setTourFund] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(Boolean(tourId));
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const [contributeDollars, setContributeDollars] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [disburseRows, setDisburseRows] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userHits, setUserHits] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);

  useEffect(() => {
    setContributeDollars(
      suggestedDollars != null && Number(suggestedDollars) > 0
        ? Number(suggestedDollars).toFixed(2)
        : ''
    );
    setDisburseRows([]);
    setAddOpen(false);
    setError('');
    setNote('');
  }, [tourId]);

  useEffect(() => {
    if (suggestedDollars != null && Number(suggestedDollars) > 0) {
      setContributeDollars((prev) =>
        String(prev || '').trim() ? prev : Number(suggestedDollars).toFixed(2)
      );
    }
  }, [suggestedDollars]);

  useEffect(() => {
    if (!tourId) {
      setTourFund(null);
      setLedger([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubFund = onSnapshot(
      doc(db, 'tour_funds', tourId),
      (snap) => {
        setTourFund(snap.exists() ? { id: snap.id, ...(snap.data() || {}) } : null);
        setLoading(false);
      },
      (err) => {
        console.warn('[TourFundSettlement] tour_funds', err);
        setLoading(false);
      }
    );
    const unsubLedger = onSnapshot(
      query(
        collection(db, 'tour_fund_ledger'),
        where('tourId', '==', tourId),
        limit(50)
      ),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        rows.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() || Number(a.createdAt) || 0;
          const tb = b.createdAt?.toMillis?.() || Number(b.createdAt) || 0;
          return tb - ta;
        });
        setLedger(rows.slice(0, 30));
      },
      (err) => console.warn('[TourFundSettlement] ledger', err)
    );
    return () => {
      unsubFund?.();
      unsubLedger?.();
    };
  }, [tourId]);

  useEffect(() => {
    const term = String(userSearch || '').trim();
    if (term.length < 2) {
      setUserHits([]);
      return undefined;
    }
    let cancelled = false;
    setSearchBusy(true);
    const t = setTimeout(() => {
      searchUsersForShare(db, term, { limitCount: 8 })
        .then((rows) => {
          if (!cancelled) setUserHits(Array.isArray(rows) ? rows : []);
        })
        .catch((err) => {
          console.warn('[TourFundSettlement] search', err);
          if (!cancelled) setUserHits([]);
        })
        .finally(() => {
          if (!cancelled) setSearchBusy(false);
        });
    }, 280);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [userSearch]);

  const heldCents = Math.max(0, Number(tourFund?.balanceCents) || 0);
  const disbursedCents = Math.max(0, Number(tourFund?.disbursedCents) || 0);
  const raisedCents = Math.max(0, Number(tourFund?.raisedCents) || 0);
  const contributePreview = useMemo(() => {
    const dollars = Number(String(contributeDollars).replace(/[^0-9.]/g, ''));
    const netCents = Math.round(dollars * 100);
    if (!Number.isFinite(netCents) || netCents < 100) return null;
    return grossUpCardChargeCents(netCents);
  }, [contributeDollars]);
  const walletTitle = `${String(tourName || tourFund?.tourName || 'Tour').trim() || 'Tour'} wallet`;

  const disburseTotalCents = useMemo(
    () =>
      disburseRows.reduce((sum, r) => {
        const dollars = Number(String(r.dollars).replace(/[^0-9.]/g, ''));
        const cents = Math.round(dollars * 100);
        return sum + (Number.isFinite(cents) && cents > 0 ? cents : 0);
      }, 0),
    [disburseRows]
  );

  const run = useCallback(async (key, fn) => {
    setError('');
    setNote('');
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      console.error(`[TourFundSettlement] ${key}`, err);
      setError(err?.message || 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  }, []);

  const addRecipient = (hit) => {
    if (!hit?.id) return;
    setDisburseRows((prev) => {
      if (prev.some((r) => r.uid === hit.id)) return prev;
      const displayName =
        [hit.firstName, hit.lastName].filter(Boolean).join(' ').trim() ||
        hit.username ||
        hit.email ||
        'VRC user';
      return [
        ...prev,
        {
          key: `${hit.id}_${Date.now()}`,
          uid: hit.id,
          email: hit.email || '',
          displayName,
          dollars: '1',
        },
      ];
    });
    setUserSearch('');
    setUserHits([]);
  };

  const addSelf = () => {
    if (!authUser?.uid) return;
    addRecipient({
      id: authUser.uid,
      email: authUser.email,
      firstName: authUser.displayName?.split?.(' ')?.[0],
      lastName: authUser.displayName?.split?.(' ')?.slice(1).join(' '),
      username: authUser.email,
    });
  };

  const handleContribute = () =>
    run('contribute', async () => {
      if (!tourId) throw new Error('Select a tour first.');
      const dollars = Number(String(contributeDollars).replace(/[^0-9.]/g, ''));
      const amountCents = Math.round(dollars * 100);
      if (!Number.isFinite(amountCents) || amountCents < 100) {
        throw new Error('Enter at least $1.00 to add.');
      }
      const origin = pageOrigin();
      const createTourFundContributionCheckout = httpsCallable(
        functions,
        'createTourFundContributionCheckout',
        { timeout: 60000 }
      );
      const result = await createTourFundContributionCheckout({
        tourId,
        amountCents,
        deployTarget,
        ...(origin
          ? {
              successUrl: `${origin}/calendar?fund=contributed`,
              cancelUrl: `${origin}/calendar?fund=cancelled`,
            }
          : {}),
      });
      const url = result?.data?.url;
      if (!url) throw new Error('Checkout URL was not returned.');
      if (Platform.OS === 'web') {
        window.location.href = url;
      } else {
        await Linking.openURL(url);
      }
    });

  const handleDisburse = () =>
    run('disburse', async () => {
      if (!tourId) throw new Error('Select a tour first.');
      if (!disburseRows.length) throw new Error('Add at least one recipient.');
      if (disburseTotalCents > heldCents) {
        throw new Error('Draft exceeds tour fund held balance.');
      }
      const allocations = disburseRows.map((r) => {
        const dollars = Number(String(r.dollars).replace(/[^0-9.]/g, ''));
        const amountCents = Math.round(dollars * 100);
        if (!Number.isFinite(amountCents) || amountCents < 1) {
          throw new Error(`Invalid amount for ${r.displayName || r.email}`);
        }
        return { recipientUid: r.uid, amountCents };
      });
      const disburseTourFund = httpsCallable(functions, 'disburseTourFund', {
        timeout: 60000,
      });
      const result = await disburseTourFund({ tourId, allocations });
      const total = result?.data?.totalCents || 0;
      setDisburseRows([]);
      setNote(
        `Disbursed ${formatFundsCents(total)} to ${allocations.length} person${
          allocations.length === 1 ? '' : 's'
        } — credited to their Wallet.`
      );
    });

  if (!tourId) {
    return (
      <View style={[styles.wrap, { borderTopColor: pal.line }]}>
        <Text style={[styles.kicker, { color: pal.mute }]}>Tour fund</Text>
        <Text style={{ color: pal.mute, fontSize: 14.5, marginTop: 8 }}>
          Select a tour to manage the shared tour fund balance.
        </Text>
      </View>
    );
  }

  return (
    <View style={[styles.wrap, { borderTopColor: pal.line }]}>
      <Text style={[styles.kicker, { color: pal.mute }]}>Tour wallet</Text>
      <Text style={[styles.hero, { color: pal.ink }]}>{walletTitle}</Text>

      {error ? (
        <View style={[styles.banner, { backgroundColor: pal.alert }]}>
          <Text style={{ color: '#fff', fontSize: 14 }}>{error}</Text>
        </View>
      ) : null}
      {note ? (
        <View style={[styles.banner, { backgroundColor: pal.ok }]}>
          <Text style={{ color: '#fff', fontSize: 14 }}>{note}</Text>
        </View>
      ) : null}

      <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
        {loading ? (
          <ActivityIndicator color={pal.ink} />
        ) : (
          <View style={styles.statRow}>
            <View style={{ minWidth: 120, flex: 1.2 }}>
              <Text style={[styles.kicker, { color: pal.mute, marginBottom: 4 }]}>Held</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <Text style={{ fontSize: 26, fontWeight: '600', color: pal.ink, letterSpacing: -0.4 }}>
                  {formatFundsCents(heldCents)}
                </Text>
                <TouchableOpacity
                  onPress={() => setAddOpen((v) => !v)}
                  disabled={Boolean(busy)}
                  accessibilityLabel="Add to tour fund"
                  style={[
                    styles.plusBtn,
                    {
                      borderColor: hue,
                      backgroundColor: addOpen ? hue : 'transparent',
                      opacity: busy ? 0.55 : 1,
                    },
                  ]}
                >
                  <Text style={{ color: addOpen ? '#fff' : hue, fontSize: 20, fontWeight: '600', lineHeight: 22 }}>
                    +
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
            <View style={{ minWidth: 100, flex: 1 }}>
              <Text style={[styles.kicker, { color: pal.mute, marginBottom: 4 }]}>Disbursed</Text>
              <Text style={{ fontSize: 26, fontWeight: '600', color: pal.ink, letterSpacing: -0.4 }}>
                {formatFundsCents(disbursedCents)}
              </Text>
            </View>
            <View style={{ minWidth: 100, flex: 1 }}>
              <Text style={[styles.kicker, { color: pal.mute, marginBottom: 4 }]}>Raised</Text>
              <Text style={{ fontSize: 26, fontWeight: '600', color: pal.ink, letterSpacing: -0.4 }}>
                {formatFundsCents(raisedCents)}
              </Text>
            </View>
          </View>
        )}

        {addOpen ? (
          <View style={[styles.addInline, { borderTopColor: pal.lineSoft }]}>
            <Text style={{ color: pal.mute, fontSize: 13.5, marginBottom: 10, lineHeight: 18, flex: 1 }}>
              Card payment into this tour pot.
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                <Text style={{ color: pal.mute, fontSize: 16 }}>$</Text>
                <TextInput
                  value={contributeDollars}
                  onChangeText={setContributeDollars}
                  keyboardType="decimal-pad"
                  autoFocus={Platform.OS === 'web'}
                  placeholder={
                    suggestedDollars != null && Number(suggestedDollars) > 0
                      ? Number(suggestedDollars).toFixed(2)
                      : '100.00'
                  }
                  placeholderTextColor={pal.faint}
                  style={[
                    styles.inlineAmount,
                    { borderColor: pal.line, backgroundColor: pal.sunk, color: pal.ink },
                  ]}
                />
              </View>
              {suggestedDollars != null && Number(suggestedDollars) > 0 ? (
                <TouchableOpacity
                  onPress={() => setContributeDollars(Number(suggestedDollars).toFixed(2))}
                >
                  <Text style={{ color: hue, fontSize: 12, fontFamily: FONT.mono }}>
                    Balance due
                  </Text>
                </TouchableOpacity>
              ) : null}
              <TouchableOpacity
                onPress={handleContribute}
                disabled={Boolean(busy)}
                style={[
                  styles.primary,
                  {
                    backgroundColor: hue,
                    opacity: busy ? 0.55 : 1,
                    paddingHorizontal: 16,
                    minHeight: 40,
                    flexGrow: 0,
                  },
                ]}
              >
                {busy === 'contribute' ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={styles.primaryText}>Pay</Text>
                )}
              </TouchableOpacity>
            </View>
            {contributePreview ? (
              <Text style={{ color: pal.mute, fontSize: 13, marginTop: 10, lineHeight: 19 }}>
                Tour fund receives {formatFundsCents(contributePreview.netCents)}. Processing fee{' '}
                {formatFundsCents(contributePreview.processingFeeCents)} — card total{' '}
                {formatFundsCents(contributePreview.chargeCents)}.
              </Text>
            ) : null}
          </View>
        ) : null}

        {!tourFund && !loading ? (
          <Text style={{ color: pal.mute, fontSize: 13.5, marginTop: 10 }}>
            No fund pot yet — Add balance.
          </Text>
        ) : null}
      </View>

      {/* Disburse */}
      <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line }]}>
        <Text style={[styles.kicker, { color: pal.mute, marginBottom: 6 }]}>Disburse from tour fund</Text>
        <Text style={{ color: pal.mute, fontSize: 14, marginBottom: 10, lineHeight: 20 }}>
          Move held cash into each person&apos;s Wallet. They withdraw to Connect separately.
        </Text>
        <Text style={{ color: pal.ink, fontFamily: FONT.mono, fontSize: 13, marginBottom: 12 }}>
          Held: {formatFundsCents(heldCents)}
          {disburseTotalCents > 0 ? `  ·  Draft: ${formatFundsCents(disburseTotalCents)}` : ''}
        </Text>

        <Text style={[styles.kicker, { color: pal.mute, marginBottom: 4 }]}>Search users</Text>
        <TextInput
          value={userSearch}
          onChangeText={setUserSearch}
          autoCapitalize="none"
          placeholder="Name or email"
          placeholderTextColor={pal.faint}
          style={[
            styles.input,
            { borderColor: pal.line, backgroundColor: pal.sunk, color: pal.ink, marginBottom: 8 },
          ]}
        />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 8, alignItems: 'center' }}>
          <TouchableOpacity
            onPress={addSelf}
            style={[styles.chip, { borderColor: pal.line }]}
          >
            <Text style={[styles.kicker, { color: pal.mute }]}>Add myself</Text>
          </TouchableOpacity>
          {searchBusy ? <ActivityIndicator color={pal.mute} /> : null}
        </View>
        {userHits.map((hit) => {
          const label =
            [hit.firstName, hit.lastName].filter(Boolean).join(' ').trim() ||
            hit.username ||
            hit.email;
          return (
            <TouchableOpacity
              key={hit.id}
              onPress={() => addRecipient(hit)}
              style={[styles.hitRow, { borderTopColor: pal.lineSoft }]}
            >
              <Text style={{ color: pal.ink, fontSize: 15, flex: 1 }} numberOfLines={1}>
                {label}
              </Text>
              <Text style={{ color: pal.mute, fontSize: 12, fontFamily: FONT.mono }} numberOfLines={1}>
                {hit.email}
              </Text>
            </TouchableOpacity>
          );
        })}

        {disburseRows.length > 0 ? (
          <View style={{ marginTop: 10 }}>
            {disburseRows.map((row) => (
              <View
                key={row.key}
                style={[styles.hitRow, { borderTopColor: pal.lineSoft, alignItems: 'center' }]}
              >
                <View style={{ flex: 1.4 }}>
                  <Text style={{ color: pal.ink, fontSize: 14.5 }} numberOfLines={1}>
                    {row.displayName}
                  </Text>
                  <Text style={{ color: pal.mute, fontSize: 12, fontFamily: FONT.mono }} numberOfLines={1}>
                    {row.email}
                  </Text>
                </View>
                <TextInput
                  value={row.dollars}
                  onChangeText={(v) =>
                    setDisburseRows((prev) =>
                      prev.map((r) => (r.key === row.key ? { ...r, dollars: v } : r))
                    )
                  }
                  keyboardType="decimal-pad"
                  style={[
                    styles.amountInput,
                    { borderColor: pal.line, backgroundColor: pal.sunk, color: pal.ink },
                  ]}
                />
                <TouchableOpacity
                  onPress={() => setDisburseRows((prev) => prev.filter((r) => r.key !== row.key))}
                  style={{ paddingHorizontal: 8, paddingVertical: 8 }}
                >
                  <Text style={[styles.kicker, { color: pal.alert }]}>Remove</Text>
                </TouchableOpacity>
              </View>
            ))}
          </View>
        ) : (
          <Text style={{ color: pal.mute, fontSize: 14, marginTop: 6 }}>
            Search and add people, then set each amount from the ledger.
          </Text>
        )}

        <TouchableOpacity
          onPress={handleDisburse}
          disabled={
            Boolean(busy) ||
            !disburseRows.length ||
            disburseTotalCents <= 0 ||
            disburseTotalCents > heldCents
          }
          style={[
            styles.primary,
            {
              backgroundColor: pal.ink,
              marginTop: 14,
              opacity:
                busy ||
                !disburseRows.length ||
                disburseTotalCents <= 0 ||
                disburseTotalCents > heldCents
                  ? 0.55
                  : 1,
            },
          ]}
        >
          {busy === 'disburse' ? (
            <ActivityIndicator color={pal.page} />
          ) : (
            <Text style={[styles.primaryText, { color: pal.page }]}>
              Disburse {disburseTotalCents > 0 ? formatFundsCents(disburseTotalCents) : ''}
            </Text>
          )}
        </TouchableOpacity>
        {disburseTotalCents > heldCents ? (
          <Text style={{ color: pal.alert, fontSize: 13, marginTop: 8 }}>
            Draft exceeds held balance — add more to the tour fund first.
          </Text>
        ) : null}
      </View>

      {/* Ledger */}
      <View style={[styles.card, { backgroundColor: pal.surf, borderColor: pal.line, padding: 0, overflow: 'hidden' }]}>
        <View style={styles.tableHead}>
          <Text style={[styles.kicker, { color: pal.mute }]}>Tour fund activity</Text>
          <Text style={{ color: pal.mute, fontSize: 13 }}>
            {ledger.length} entr{ledger.length === 1 ? 'y' : 'ies'}
          </Text>
        </View>
        <View style={[styles.tierHead, { borderTopColor: pal.lineSoft }]}>
          <Text style={[styles.colH, { color: pal.mute, width: 130 }]}>Type</Text>
          <Text style={[styles.colH, { color: pal.mute, width: 90, textAlign: 'right' }]}>Amount</Text>
          <Text style={[styles.colH, { color: pal.mute, flex: 1, paddingLeft: 12 }]}>Ref</Text>
        </View>
        {ledger.length === 0 ? (
          <Text style={{ color: pal.mute, padding: 18, fontSize: 14.5 }}>
            No contributions or disbursements yet for this tour.
          </Text>
        ) : (
          ledger.map((row) => {
            const out = row.type === 'disbursement' || row.type === 'payout';
            return (
              <View key={row.id} style={[styles.tierRow, { borderTopColor: pal.lineSoft }]}>
                <Text style={{ width: 130, color: pal.ink, fontSize: 14 }}>{ledgerTypeLabel(row.type)}</Text>
                <Text
                  style={{
                    width: 90,
                    textAlign: 'right',
                    color: out ? pal.alert : pal.ink,
                    fontFamily: FONT.mono,
                    fontSize: 14,
                  }}
                >
                  {out ? '−' : '+'}
                  {formatFundsCents(row.amountCents)}
                </Text>
                <Text
                  style={{
                    flex: 1,
                    paddingLeft: 12,
                    color: pal.mute,
                    fontFamily: FONT.mono,
                    fontSize: 11.5,
                  }}
                  numberOfLines={1}
                >
                  {row.recipientUid ||
                    row.stripeCheckoutSessionId ||
                    row.batchId ||
                    row.id}
                </Text>
              </View>
            );
          })
        )}
      </View>
    </View>
  );
}

const styles = {
  wrap: {
    marginTop: 28,
    paddingTop: 24,
    borderTopWidth: 2,
  },
  kicker: {
    fontFamily: FONT.mono,
    fontSize: 12,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  hero: {
    fontSize: 26,
    fontWeight: '700',
    letterSpacing: -0.4,
    marginTop: 4,
  },
  card: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 18,
    marginTop: 14,
  },
  banner: {
    marginTop: 12,
    padding: 12,
    borderRadius: 9,
  },
  statRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  plusBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addInline: {
    marginTop: 14,
    paddingTop: 14,
    borderTopWidth: 1,
  },
  inlineAmount: {
    width: 110,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 16,
    fontFamily: FONT.mono,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    marginBottom: 12,
  },
  primary: {
    paddingVertical: 12,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  primaryText: {
    color: '#fff',
    fontWeight: '600',
    textAlign: 'center',
  },
  chip: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
    borderWidth: 1,
  },
  hitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    borderTopWidth: 1,
  },
  amountInput: {
    width: 100,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 15,
    textAlign: 'right',
    fontFamily: FONT.mono,
  },
  tableHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 18,
    paddingVertical: 14,
  },
  tierHead: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 18,
    paddingBottom: 8,
    borderTopWidth: 1,
    paddingTop: 10,
  },
  tierRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderTopWidth: 1,
  },
  colH: {
    fontFamily: FONT.mono,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
};
